import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { formatPhoneNumber } from '../../../utils/format-phone';

@Component({
  selector: 'app-mb-input',
  imports: [
    CommonModule,
    NzInputModule,
    FormsModule,
    NzIconModule,
    TranslocoDirective
],
  standalone: true,
  templateUrl: './mb-input.component.html',
  styleUrls: ['./mb-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MbInputComponent),
      multi: true,
    },
  ],
})
export class MbInputComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() prefixLabel = '';
  @Input() label = '';
  @Input() type = 'text';
  @Input() icon = '';
  @Input() containerClass = '';
  @Input() errorMessage = '';
  @Input() hasError = false;
  @Input() set disabled(value: boolean) {
    this.inputDisabled = !!value;
  }
  get disabled(): boolean {
    return this.inputDisabled || this.controlDisabled;
  }

  value = '';
  displayValue = '';
  showPassword = false;
  private inputDisabled = false;
  private controlDisabled = false;

  // ControlValueAccessor methods
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (_value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched = () => {};

  get containerClasses(): string {
    return `${this.containerClass}`;
  }

  get inputType(): string {
    return this.type === 'password' && this.showPassword ? 'text' : this.type;
  }

  get inputClasses(): string {
    return this.hasError ? 'error-input' : '';
  }

  get isPhoneInput(): boolean {
    return this.type === 'phone';
  }

  get showPhonePrefix(): boolean {
    return this.isPhoneInput;
  }

  writeValue(value: string): void {
    this.value = value || '';
    if (this.isPhoneInput) {
      this.updatePhoneDisplayValue();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
  }

  onValueChange(inputValue: string): void {
    if (this.isPhoneInput) {
      this.handlePhoneInput(inputValue);
    } else {
      this.value = inputValue;
      this.onChange(inputValue);
      this.onTouched();
    }
  }

  private handlePhoneInput(inputValue: string): void {
    const digitsOnly = inputValue.replace(/\D/g, '');
    const cleanDigits = digitsOnly.substring(0, 9);

    this.displayValue = formatPhoneNumber(cleanDigits);

    const fullNumber = cleanDigits ? `+250${cleanDigits}` : '';
    this.value = fullNumber;

    this.onChange(fullNumber);
    this.onTouched();
  }

  private updatePhoneDisplayValue(): void {
    if (!this.value) {
      this.displayValue = '';
      return;
    }

    const digitsOnly = this.value
      .replace(/^\+250/, '')
      .replace(/\D/g, '')
      .substring(0, 9);
    this.displayValue = formatPhoneNumber(digitsOnly);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
